import {
  isPair,
  type AchievementProgress,
  type Bracket,
  type ChatLine,
  type ClassPair,
  type ClientMessage,
  type MatchSummaryUnit,
  type PartyInfo,
  type Rating,
  type RosterUnit,
  type ServerMessage,
} from '@arena/shared';
import type { ArenaMap } from '@arena/shared';
import { challenge } from '../game/Sfx';
import { barFor, settings, talentsFor, utilityBar } from './settings.svelte';

export interface MatchInfo {
  matchId: string;
  bracket: Bracket | 'practice' | 'private' | 'hub';
  map: ArenaMap;
  youId: string;
  /** Absent for the hub, which runs on the lobby socket. */
  server?: string;
  ticket?: string;
}

/** Lobby-level state the UI renders. Snapshots skip this and go straight to the game. */
export const app = $state({
  status: 'connecting' as 'connecting' | 'online' | 'offline',
  /** Our place in line while the game is full; null once we're in. */
  line: null as { position: number; size: number } | null,
  user: null as { id: string; name: string; guest: boolean } | null,
  classes: ['warrior', 'mage'] as ClassPair,
  ratings: [] as Rating[],
  achievements: {
    earned: {},
    stats: {},
    title: null,
    trail: null,
    offTrail: null,
  } as AchievementProgress,
  online: 0,
  queued: { '1v1': 0, '2v2': 0, '3v3': 0 } as Record<Bracket, number>,
  fighting: 0,
  /** The numbered hub we're in, or were in before this match; 0 until the first `hub` message. */
  hub: 0,
  /** Round trip in ms to the server we're playing on: the match server in a match, else the lobby. */
  ping: null as number | null,
  chat: [] as ChatLine[],
  party: null as PartyInfo | null,
  /** Name of whoever is inviting us to their party, until we answer. */
  invite: null as string | null,
  /** Whoever is challenging us (or our party, for a team duel) to a duel, until we answer. */
  duel: null as { from: string; team: boolean } | null,
  queue: { bracket: null as Bracket | null, since: null as number | null },
  match: null as MatchInfo | null,
  /** Both sides of the current match, from the match server. */
  roster: null as [RosterUnit[], RosterUnit[]] | null,
  result: null as { winner: 0 | 1 | null; units: MatchSummaryUnit[] } | null,
  error: null as string | null,
});

type Listener = (message: ServerMessage) => void;
const listeners = new Set<Listener>();
/** The lobby: hub, chat, parties, queues. */
let socket: WebSocket | null = null;
let retry: ReturnType<typeof setTimeout> | undefined;
/** The match server, open only while in a match. */
let matchSocket: WebSocket | null = null;
let matchRetry: ReturnType<typeof setTimeout> | undefined;

export function onMessage(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function send(message: ClientMessage): void {
  if (socket?.readyState === WebSocket.OPEN)
    socket.send(JSON.stringify(message));
}

// The lobby hands these bars and talents to the match server, which allows casting only what's on the bars; practice also takes changes mid-match. `user` is a new object on every welcome, so a reconnect resends them.
$effect.root(() => {
  $effect(() => {
    if (!app.user) return;
    const messages: ClientMessage[] = [
      ...app.classes.map((classId): ClientMessage => ({
        t: 'setBar',
        classId,
        abilities: barFor(classId),
      })),
      ...app.classes.map((classId): ClientMessage => ({
        t: 'setTalents',
        classId,
        talents: talentsFor(classId),
      })),
      { t: 'setUtility', abilities: utilityBar() },
    ];
    for (const message of messages) {
      send(message);
      if (app.match?.bracket === 'practice') sendMatch(message);
    }
  });
});

export function pickClasses(classes: ClassPair): void {
  app.classes = classes;
  settings.classes = classes;
  send({ t: 'setClasses', classes });
}

export function sendMatch(message: ClientMessage): void {
  if (matchSocket?.readyState === WebSocket.OPEN)
    matchSocket.send(JSON.stringify(message));
}

/** Resolves a path against the page's host, as a WebSocket URL. */
function socketUrl(url: string): string {
  if (!url.startsWith('/')) return url;
  const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${protocol}://${location.host}${url}`;
}

setInterval(() => {
  const message: ClientMessage = { t: 'ping', at: performance.now() };
  if (app.match) sendMatch(message);
  else send(message);
}, 2000);

function receive(event: MessageEvent): void {
  const message = JSON.parse(event.data) as ServerMessage;
  apply(message);
  for (const listener of listeners) listener(message);
}

export function connect(): void {
  clearTimeout(retry);
  socket?.close();
  const ws = new WebSocket(socketUrl('/ws'));
  socket = ws;
  app.status = 'connecting';
  ws.onopen = () => (app.status = 'online');
  // 4000: connected elsewhere; 4001: removed by a moderator; 4002: away too long. None reconnects on its own.
  ws.onclose = (event) => {
    if (socket !== ws) return;
    app.status = 'offline';
    const system = (text: string) =>
      (app.chat = [...app.chat, { channel: 'system', text, at: Date.now() }]);
    if (event.code === 4001) system('A moderator disconnected you.');
    else if (event.code === 4002) {
      system(
        'You were disconnected for being away. Press any key or click to come back.',
      );
      const back = () => socket === ws && connect();
      window.addEventListener('keydown', back, { once: true });
      window.addEventListener('pointerdown', back, { once: true });
    } else if (event.code !== 4000) retry = setTimeout(connect, 2000);
  };
  ws.onmessage = receive;
}

/** Set once the lobby runs a newer build than this tab; it reloads as soon as it's out of a match. */
let staleBuild = '';

function reloadIfStale(): void {
  if (!staleBuild || app.match) return;
  // Once per build, so a lobby and client that disagree can't reload forever.
  sessionStorage.setItem('reloadedFor', staleBuild);
  location.reload();
}

const ignoring = (name: string | undefined) =>
  !!name && settings.ignored.includes(name.toLowerCase());

/** 4004: the match server doesn't know this match (it ended or restarted), so go back to the hub. */
function connectMatch(match: MatchInfo): void {
  clearTimeout(matchRetry);
  matchSocket?.close();
  const ws = new WebSocket(
    `${socketUrl(match.server!)}?ticket=${encodeURIComponent(match.ticket!)}`,
  );
  matchSocket = ws;
  ws.onclose = (event) => {
    if (matchSocket !== ws) return;
    matchSocket = null;
    if (app.result || event.code === 4000) return;
    if (event.code === 4004) return leaveMatch();
    matchRetry = setTimeout(() => {
      if (app.match?.matchId === match.matchId) connectMatch(app.match);
    }, 1000);
  };
  ws.onmessage = receive;
}

/** Matches start the moment the queue pops, so a player who tabbed away while waiting hears it and sees it on the tab. */
function announceMatch(): void {
  challenge();
  if (!document.hidden) return;
  const title = document.title;
  document.title = 'Match found! · Sandsworn';
  document.addEventListener(
    'visibilitychange',
    () => (document.title = title),
    {
      once: true,
    },
  );
  if (
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted'
  )
    new Notification('Sandsworn', { body: 'Your match is starting.' });
}

function apply(message: ServerMessage): void {
  switch (message.t) {
    case 'line':
      app.line = { position: message.position, size: message.size };
      break;
    case 'welcome':
      app.line = null;
      app.user = message.user;
      app.classes = message.classes;
      app.ratings = message.ratings;
      app.chat = [];
      // The server forgets the classes between sessions; stale ones from an older build are dropped.
      if (
        settings.classes &&
        isPair(settings.classes) &&
        settings.classes.join() !== message.classes.join()
      )
        pickClasses(settings.classes);
      if (
        __BUILD__ &&
        message.build &&
        message.build !== __BUILD__ &&
        sessionStorage.getItem('reloadedFor') !== message.build
      )
        staleBuild = message.build;
      reloadIfStale();
      break;
    case 'lobby':
      app.online = message.online;
      app.queued = message.queued;
      app.fighting = message.fighting;
      break;
    case 'pong':
      app.ping = Math.round(performance.now() - message.at);
      break;
    case 'chat':
      if (message.line.fromId !== app.user?.id && ignoring(message.line.from))
        break;
      app.chat = [...app.chat.slice(-99), message.line];
      break;
    case 'party':
      app.party = message.party;
      break;
    case 'partyInvite':
      if (ignoring(message.from)) send({ t: 'partyDecline' });
      else app.invite = message.from;
      break;
    case 'duelRequest':
      if (ignoring(message.from)) send({ t: 'duelDecline' });
      else app.duel = { from: message.from, team: message.team };
      break;
    case 'queue':
      app.queue = { bracket: message.bracket, since: message.since };
      break;
    case 'matchStart': {
      // The lobby repeats this with a fresh ticket when we reconnect to it mid-match.
      const same = app.match?.matchId === message.matchId;
      if (!same) {
        app.result = null;
        app.ping = null;
        app.roster = null;
        if (message.bracket !== 'practice') announceMatch();
      }
      app.match = {
        matchId: message.matchId,
        bracket: message.bracket,
        map: message.map,
        youId: message.youId,
        server: message.server,
        ticket: message.ticket,
      };
      if (!same || !matchSocket) connectMatch(app.match);
      break;
    }
    case 'matchEnd':
      app.result = { winner: message.winner, units: message.units };
      break;
    case 'ratings':
      app.ratings = message.ratings;
      break;
    case 'achievements':
      app.achievements = message.progress;
      break;
    case 'error':
      app.error = message.message;
      setTimeout(() => {
        if (app.error === message.message) app.error = null;
      }, 4000);
      break;
    case 'roster':
      app.roster = message.teams;
      break;
    case 'hub':
      app.hub = message.hub;
      break;
    case 'snap':
    case 'hubSelf':
      break;
  }
}

export function leaveMatch(): void {
  send({ t: 'leaveMatch' });
  clearTimeout(matchRetry);
  const ws = matchSocket;
  matchSocket = null;
  ws?.close();
  app.match = null;
  app.result = null;
  app.ping = null;
  app.roster = null;
  reloadIfStale();
}
